import React, { useState } from 'react';
import {
  Alert, Box, Button, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper,
  Stack, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import RemoveIcon from '@mui/icons-material/Remove';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import InventoryOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { SECTIONS, money, newGuid, number, putAwayCountText, quantity, sectionText, weighedPrice } from '../labels';
import { Loader, SectionTitle, useConfirm, useLoad, useNotify, useUndoable } from '../common';
import CartEntryDialog, { CartEntryMode } from './CartEntryDialog';
import ExtraDialog from './ExtraDialog';
import { isOpenTrip, tripTitle, useTripOptions } from './tripTools';

const GLYPHS: Record<T.TripItemState, React.ReactElement> = {
  InList: <RadioButtonUncheckedIcon color="primary" />,
  InCart: <ShoppingCartIcon color="primary" />,
  Purchased: <CheckCircleIcon color="success" />,
  Unavailable: <CloseIcon color="disabled" />,
  Replaced: <SwapHorizIcon color="disabled" />,
  NotPurchased: <RemoveIcon color="disabled" />,
};

const isInactive = (state: T.TripItemState) => state === 'Unavailable' || state === 'Replaced' || state === 'NotPurchased';
const inCart = (state: T.TripItemState) => state === 'InCart' || state === 'Purchased';

function itemAmount(item: T.TripItem): string {
  return inCart(item.state) ? money(item.receiptLineTotal ?? item.lineTotal) : quantity(item.plannedQuantity, item.unit);
}

function itemSubtitle(item: T.TripItem, substitutesName?: string): string {
  const parts: string[] = [];
  switch (item.state) {
    case 'InCart':
    case 'Purchased':
      if (item.isWeighed && item.weight != null) parts.push(weighedPrice(item.weight, item.shelfUnitPrice, item.lineTotal));
      else if (item.shelfUnitPrice != null) parts.push(`${number(item.cartQuantity ?? 1)} × ${money(item.shelfUnitPrice)}`);
      if (item.isPromo) parts.push(item.regularUnitPrice != null ? `promo (normal ${money(item.regularUnitPrice)})` : 'promo');
      if (item.hasPriceMismatch) parts.push(`⚠️ talão ${money(item.receiptLineTotal ?? 0)}`);
      break;
    case 'Unavailable': parts.push('não havia'); break;
    case 'Replaced': parts.push('trocado por outro artigo'); break;
    case 'NotPurchased': parts.push('não comprado'); break;
    default:
      if (item.lastShelfPrice != null) parts.push(`última vez ${money(item.lastShelfPrice)}`);
  }
  if (item.isUnplanned) parts.push('fora da lista');
  if (substitutesName) parts.push(`em vez de ${substitutesName}`);
  if (item.meals.length > 0 && item.state === 'InList') parts.push(item.meals.join(', '));
  return parts.join(' · ');
}

const stateRank = (state: T.TripItemState) => (state === 'InList' ? 0 : inCart(state) ? 1 : 2);

/** Lines by aisle in the store's order: listed first, then in the cart, then the rest. */
function sectionsOf(trip: T.Trip): { section: T.StoreSection; items: T.TripItem[] }[] {
  const order = trip.sectionOrder.length > 0 ? trip.sectionOrder : SECTIONS.map(s => s.value);
  return order
    .map(section => ({
      section,
      items: trip.items
        .filter(i => i.storeSection === section)
        .sort((a, b) => stateRank(a.state) - stateRank(b.state) || a.name.localeCompare(b.name, 'pt-PT', { sensitivity: 'base' })),
    }))
    .filter(s => s.items.length > 0);
}

/**
 * Store mode for one trip: click a listed line to put it in the cart at the shelf price;
 * its menu has "Não havia" and "Outra coisa". Every action is one sync change.
 */
const TripPage: React.FC = () => {
  const { tripId = '' } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const offerUndo = useUndoable();
  const [confirm, confirmDialog] = useConfirm();
  const { data: trip, setData: setTrip, error, loading, reload } = useLoad(() => api.getTrip(tripId), [tripId]);
  const [entry, setEntry] = useState<{ mode: CartEntryMode; item: T.TripItem | null } | null>(null);
  const [lineMenu, setLineMenu] = useState<{ anchor: HTMLElement; item: T.TripItem } | null>(null);
  const [addMenu, setAddMenu] = useState<HTMLElement | null>(null);
  const [extraOpen, setExtraOpen] = useState(false);
  const options = useTripOptions(updated => {
    if (updated) setTrip(updated);
    else navigate('/casa/idas');
  });

  /** Sends one change; returns the server's note when it was not applied. */
  const sync = async (change: T.TripChangeRequest): Promise<string | null> => {
    const result = await api.syncTrip(tripId, [change]);
    setTrip(result.trip);
    return result.results.find(r => !r.applied)?.note ?? (result.results.some(r => !r.applied) ? 'A alteração não foi aplicada.' : null);
  };

  const simple = async (kind: T.TripChangeKind, item: T.TripItem) => {
    try {
      const note = await sync({ clientChangeId: newGuid(), kind, itemPublicId: item.publicId });
      if (note) notify(`${item.name}: ${note}`, { severity: 'warning' });
    } catch (e: any) {
      notify(e.message ?? 'Ocorreu um erro.', { severity: 'error' });
    }
  };

  const open = trip ? isOpenTrip(trip) : false;

  const addExtra = (extra: T.TripExtraRequest) =>
    sync({ clientChangeId: newGuid(), kind: 'Extra', newItemTempId: newGuid(), extra });

  /** Out of the cart, with "Desfazer" to put it back. */
  const removeExtra = async (extra: T.TripExtra) => {
    try {
      const note = await sync({ clientChangeId: newGuid(), kind: 'RemoveExtra', itemPublicId: extra.publicId });
      if (note) {
        notify(`${extra.description}: ${note}`, { severity: 'warning' });
        return;
      }
      offerUndo(`${extra.description}: fora do carrinho`,
        () => addExtra({ description: extra.description, quantity: extra.quantity, unitPrice: extra.unitPrice }));
    } catch (e: any) {
      notify(e.message ?? 'Ocorreu um erro.', { severity: 'error' });
    }
  };

  const onItemClick = async (item: T.TripItem, anchor: HTMLElement) => {
    if (!open) return;
    switch (item.state) {
      case 'InList': setEntry({ mode: 'cart', item }); break;
      case 'InCart': setLineMenu({ anchor, item }); break;
      case 'Unavailable':
        if (await confirm({ title: item.name, text: 'Afinal havia?', confirmText: 'Sim, voltar à lista', cancelText: 'Não' }))
          await simple('Restore', item);
        break;
      default: break;
    }
  };

  const pay = () => {
    if (!trip) return;
    if (!trip.items.some(i => i.state === 'InCart') && trip.extras.length === 0) {
      notify('O carrinho está vazio.', { severity: 'info' });
      return;
    }
    navigate(`/casa/idas/${tripId}/pagar`);
  };

  const names = new Map<string, string>();
  (trip?.items ?? []).forEach(i => names.set(i.publicId, i.name));

  return (
    <>
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <IconButton aria-label="Voltar" onClick={() => navigate('/casa/idas')} sx={{ mt: -0.5 }}><ArrowBackIcon /></IconButton>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>{trip ? tripTitle(trip) : 'Modo loja'}</Typography>
          {open && (
            <Typography variant="body2" color="text.secondary">
              Clica para pôr no carrinho com o preço da prateleira. No menu ⋮ de cada artigo: «não havia» ou «outra coisa».
            </Typography>
          )}
        </Box>
        {trip && open && (
          <IconButton
            aria-label="Opções da ida"
            onClick={() => options.open(trip, trip.items.every(i => !inCart(i.state)))}
          >
            <MoreVertIcon />
          </IconButton>
        )}
      </Stack>

      <Loader loading={loading} error={error} onRetry={reload} hasData={trip !== undefined}>
        {trip && (
          <>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            {trip.items.length === 0 && trip.extras.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Esta ida ainda não tem artigos. Junta algo com «+ Carrinho».
              </Typography>
            )}
            {sectionsOf(trip).map(({ section, items }) => (
              <React.Fragment key={section}>
                <SectionTitle>{sectionText(section)}</SectionTitle>
                <Paper variant="outlined">
                  <List disablePadding>
                    {items.map(item => (
                      <ListItem
                        key={item.publicId}
                        divider
                        disablePadding
                        secondaryAction={open && item.state === 'InList' ? (
                          <IconButton edge="end" aria-label={`Mais opções para ${item.name}`} onClick={e => setLineMenu({ anchor: e.currentTarget, item })}>
                            <MoreVertIcon />
                          </IconButton>
                        ) : undefined}
                      >
                        <ListItemButton onClick={e => onItemClick(item, e.currentTarget)}>
                          <ListItemIcon sx={{ minWidth: 40 }}>{GLYPHS[item.state]}</ListItemIcon>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} justifyContent="space-between">
                                <Box component="span" sx={{ color: isInactive(item.state) ? 'text.disabled' : undefined, minWidth: 0, overflowWrap: 'anywhere' }}>
                                  {item.name}
                                </Box>
                                <Box component="span" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{itemAmount(item)}</Box>
                              </Stack>
                            }
                            secondary={itemSubtitle(item, item.substitutesItemPublicId ? names.get(item.substitutesItemPublicId) : undefined) || undefined}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </React.Fragment>
            ))}

            {trip.extras.length > 0 && (
              <>
                <SectionTitle count={trip.extras.length}>Avulsos</SectionTitle>
                <Paper variant="outlined">
                  <List disablePadding>
                    {trip.extras.map(extra => (
                      <ListItem
                        key={extra.publicId}
                        divider
                        secondaryAction={open ? (
                          <IconButton edge="end" aria-label={`Tirar ${extra.description} do carrinho`} onClick={() => removeExtra(extra)}>
                            <CloseIcon />
                          </IconButton>
                        ) : undefined}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>{GLYPHS[open ? 'InCart' : 'Purchased']}</ListItemIcon>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} justifyContent="space-between">
                              <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{extra.description}</Box>
                              <Box component="span" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{money(extra.lineTotal)}</Box>
                            </Stack>
                          }
                          secondary={extra.quantity === 1 ? 'avulso' : `avulso · ${number(extra.quantity)} × ${money(extra.unitPrice)}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </>
            )}

            <Paper
              elevation={4}
              sx={{ position: 'sticky', bottom: { xs: 64, md: 8 }, mt: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', zIndex: 2 }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="h6" component="div" sx={{ lineHeight: 1.3 }}>
                  {trip.status === 'Paid' ? `Pago: ${money(trip.receiptTotal ?? trip.cartTotal)}` : `Carrinho: ${money(trip.cartTotal)}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {open
                    ? (trip.pendingCount === 1 ? 'falta 1 artigo' : `faltam ${trip.pendingCount} artigos`)
                    : trip.status === 'Paid' ? (trip.toPutAwayCount > 0 ? putAwayCountText(trip.toPutAwayCount) : 'paga') : 'cancelada'}
                </Typography>
              </Box>
              {open && (
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" startIcon={<AddShoppingCartIcon />} onClick={e => setAddMenu(e.currentTarget)}>
                    Carrinho
                  </Button>
                  <Button variant="contained" onClick={pay}>Pagar</Button>
                </Stack>
              )}
              {trip.status === 'Paid' && trip.toPutAwayCount > 0 && (
                <Button variant="contained" startIcon={<InventoryOutlinedIcon />} onClick={() => navigate(`/casa/idas/${tripId}/arrumar`)}>
                  Arrumar
                </Button>
              )}
            </Paper>
          </>
        )}
      </Loader>

      <Menu anchorEl={lineMenu?.anchor} open={lineMenu !== null} onClose={() => setLineMenu(null)}>
        {lineMenu?.item.state === 'InList' && [
          <MenuItem key="cart" onClick={() => { const item = lineMenu.item; setLineMenu(null); setEntry({ mode: 'cart', item }); }}>
            Pôr no carrinho
          </MenuItem>,
          <MenuItem key="unavailable" onClick={() => { const item = lineMenu.item; setLineMenu(null); simple('Unavailable', item); }}>
            Não havia
          </MenuItem>,
          <MenuItem key="replace" onClick={() => { const item = lineMenu.item; setLineMenu(null); setEntry({ mode: 'replace', item }); }}>
            Outra coisa
          </MenuItem>,
        ]}
        {lineMenu?.item.state === 'InCart' && [
          <MenuItem key="fix" onClick={() => { const item = lineMenu.item; setLineMenu(null); setEntry({ mode: 'cart', item }); }}>
            Corrigir o preço
          </MenuItem>,
          <MenuItem key="uncart" onClick={() => { const item = lineMenu.item; setLineMenu(null); simple('Uncart', item); }}>
            Tirar do carrinho
          </MenuItem>,
        ]}
      </Menu>

      <Menu anchorEl={addMenu} open={addMenu !== null} onClose={() => setAddMenu(null)}>
        <MenuItem onClick={() => { setAddMenu(null); setEntry({ mode: 'unplanned', item: null }); }}>
          <ListItemText primary="Artigo" secondary="Fica no catálogo, no stock e no histórico de preços" />
        </MenuItem>
        <MenuItem onClick={() => { setAddMenu(null); setExtraOpen(true); }}>
          <ListItemText primary="Avulso" secondary="Decoração, utensílios…: só conta para o total" />
        </MenuItem>
      </Menu>

      <ExtraDialog open={extraOpen} onClose={() => setExtraOpen(false)} onSubmit={addExtra} />
      <CartEntryDialog
        open={entry !== null}
        mode={entry?.mode ?? 'cart'}
        item={entry?.item ?? null}
        onClose={() => setEntry(null)}
        onSubmit={sync}
      />
      {confirmDialog}
      {options.element}
    </>
  );
};

export default TripPage;
